import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { QaiFieldComponent, QaiTextareaComponent, QaiTrimLeadingDirective, QaiPasteCleanDirective } from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { DetailsCardComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-card/details-card.component';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, Observable, Subject, of } from 'rxjs';
import { catchError, concatMap, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { ISimulationPrompt } from 'src/app/interfaces/self-serve/isimulation-prompt';

const ownedPromptUuids = new Map<string, string>();

@Component({
  selector: 'clean-transcript-prompt',
  standalone: true,
  templateUrl: './clean-transcript-prompt.component.html',
  styleUrls: ['./clean-transcript-prompt.component.scss'],
  imports: [
    QaiFieldComponent,
    FormsModule,
    CommonModule,
    QaiTextareaComponent,
    QaiTrimLeadingDirective,
    DetailsCardComponent,
    ReactiveFormsModule,
    QaiPasteCleanDirective,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class CleanTranscriptPromptComponent implements OnInit {
  cleanTranscriptPromptGroup!: FormGroup;
  callUuid: string = '';

  private destroyRef = inject(DestroyRef);
  // Saves run one at a time so a save never starts before the previous one patched the new uuid
  private saveQueue$ = new Subject<void>();
  // An edit still inside the autosave debounce when the tab is left
  private hasPendingSave = false;

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authorReactiveFormService: AuthorReactiveFormService
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.setupSaveQueue();
    this.loadFormAndSetupAutoSave();
    this.setReadOnlyMode();
  }

  private setReadOnlyMode() {
    this.authorReactiveFormService.setReadOnlyMode(
      this.callUuid,
      this.cleanTranscriptPromptGroup?.get('promptText')
    );
  }

  private loadFormAndSetupAutoSave(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((form) => {
        this.cleanTranscriptPromptGroup = form.get(
          SimulationConstants.CLEAN_TRANSCRIPT_PROMPT_PATH
        ) as FormGroup;
        this.setupAutoSave(
          this.cleanTranscriptPromptGroup,
          SimulationConstants.CLEAN_TRANSCRIPT_PROMPT_PATH
        );
      });
  }

  private setupSaveQueue(): void {
    this.saveQueue$
      .pipe(
        concatMap(() =>
          this.triggerPromptSave(
            this.callUuid,
            this.cleanTranscriptPromptGroup.getRawValue(),
            SimulationConstants.CLEAN_TRANSCRIPT_PROMPT_PATH
          ).pipe(
            catchError(() => EMPTY) // keep the queue alive after a failed save
          )
        )
      )
      .subscribe();

    // Completing instead of unsubscribing lets queued and in-flight saves finish after the tab is left
    this.destroyRef.onDestroy(() => {
      if (this.hasPendingSave && !this.authorReactiveFormService.isReadOnly(this.callUuid)) {
        this.saveQueue$.next();
      }
      this.saveQueue$.complete();
    });
  }

  private setupAutoSave(section: FormGroup, formPath: string): void {
    const promptTextControl = section?.get('promptText');

    if (!promptTextControl) return;

    let previousValue: string = promptTextControl.getRawValue();

    promptTextControl.valueChanges
      .pipe(
        tap(() => (this.hasPendingSave = true)),
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((newVal) => {
        this.hasPendingSave = false;
        const oldValue = previousValue;
        previousValue = newVal;

        const updatedValue = section.getRawValue();

        const trimmedOld = (oldValue || '').trim();
        const trimmedNew = (newVal || '').trim();

        if (trimmedOld === trimmedNew && trimmedNew === '') {
          return;
        }

        const readOnly = this.authorReactiveFormService.isReadOnly(
          this.callUuid
        );

        this.authorReactiveFormService.patchForm(
          this.callUuid,
          formPath,
          updatedValue
        );

        if (updatedValue && !readOnly) {
          this.saveQueue$.next();
        }
      });
  }

  private triggerPromptSave(
    callUuid: string,
    prompt: ISimulationPrompt,
    formPath: string
  ): Observable<unknown> {
    const isCleared = !(prompt.promptText || '').trim();

    let request$: Observable<string | null>;

    if (isCleared) {
      // An empty promptText makes updatePrompts unlink the prompt from the call without editing it
      if (!prompt.uuid) return EMPTY;
      request$ = this.unlinkPrompt(callUuid, prompt).pipe(map(() => null));
    } else if (prompt.uuid && prompt.uuid === ownedPromptUuids.get(callUuid)) {
      request$ = this.authorService
        .updateSimulationPrompts(callUuid, [prompt])
        .pipe(map((response) => response?.prompts?.[0]?.uuid ?? null));
    } else {
      request$ = this.authorService
        .createSimulationPrompts(callUuid, [
          {
            ...prompt,
            uuid: null,
            promptType: SimulationConstants.CLEAN_TRANSCRIPT_PROMPT_TYPE,
          },
        ])
        .pipe(
          map((response) => response?.prompts?.[0]?.uuid ?? null),
          switchMap((createdUuid) =>
            createdUuid && prompt.uuid
              ? this.unlinkPrompt(callUuid, prompt).pipe(map(() => createdUuid))
              : of(createdUuid)
          )
        );
    }

    return request$.pipe(
      tap((uuid) => {
        if (!isCleared && !uuid) return;

        if (uuid) {
          ownedPromptUuids.set(callUuid, uuid);
        } else {
          ownedPromptUuids.delete(callUuid);
        }
        this.authorReactiveFormService.patchForm(callUuid, formPath, { uuid });
      })
    );
  }

  private unlinkPrompt(
    callUuid: string,
    prompt: ISimulationPrompt
  ): Observable<unknown> {
    return this.authorService.updateSimulationPrompts(callUuid, [
      { ...prompt, promptText: '' },
    ]);
  }
}
